/**
 * Date filter value editor for the Data Grid filter panel.
 * Replaces native <input type="date"> (browser-locale placeholders like 年/月/日)
 * with MUI DatePicker using DD-MMM-YYYY, consistent with other app pickers.
 */
import * as React from "react";
import dayjs, { type Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useGridRootProps } from "@mui/x-data-grid-premium";
import type { GridFilterInputValueProps } from "@mui/x-data-grid/models/gridFilterInputComponent";
import type { TextFieldProps } from "@mui/material/TextField";
import { appDatePickerSlots } from "../../shared/datePickerSlots";

const DISPLAY_FORMAT = "DD-MMM-YYYY";

export type GridFilterDatePickerInputProps =
  GridFilterInputValueProps<TextFieldProps>;

export function GridFilterDatePickerInput(props: GridFilterDatePickerInputProps) {
  const { item, applyValue, apiRef, focusElementRef, disabled } = props;
  const rootProps = useGridRootProps();
  const debounceMs = rootProps.filterDebounceMs ?? 300;

  const dayjsValue = React.useMemo((): Dayjs | null => {
    if (item.value == null) return null;
    const d = dayjs(item.value as string | Date | number);
    return d.isValid() ? d : null;
  }, [item.value]);

  const [draft, setDraft] = React.useState<Dayjs | null>(dayjsValue);
  React.useEffect(() => {
    setDraft(dayjsValue);
  }, [dayjsValue]);

  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  React.useEffect(
    () => () => {
      if (timeoutRef.current !== undefined) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const scheduleApply = React.useCallback(
    (next: Dayjs | null) => {
      setDraft(next);
      if (timeoutRef.current !== undefined) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        applyValue({
          ...item,
          value: next?.isValid() ? next.toDate() : undefined,
        });
      }, debounceMs);
    },
    [applyValue, item, debounceMs],
  );

  const label =
    apiRef.current?.getLocaleText("filterPanelInputLabel") ?? "Value";

  return (
    <DatePicker
      format={DISPLAY_FORMAT}
      value={draft}
      onChange={(v) => scheduleApply(v)}
      disabled={disabled}
      slots={appDatePickerSlots}
      slotProps={{
        textField: {
          size: "small",
          fullWidth: true,
          inputRef: focusElementRef,
          label,
        },
      }}
    />
  );
}
